import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Save, Calculator, AlertTriangle } from "lucide-react";
import { businessApi } from "../../lib/api";

// Postal code is on Sjælland if it's 1000-4999 (rough heuristic)
function isSjælland(pc) {
  const n = parseInt(pc, 10);
  return !isNaN(n) && n >= 1000 && n <= 4999;
}

const MAX_WEIGHT = 11;

export default function BusinessShipmentCreatePage() {
  const nav = useNavigate();
  const [locations, setLocations] = React.useState([]);
  const [f, setF] = React.useState({
    customer_reference: "",
    pickup_location_id: "",
    product: "next_day_b2b",
    recipient_name: "",
    recipient_phone: "",
    recipient_email: "",
    delivery_address: "",
    delivery_postal_code: "",
    delivery_city: "",
    delivery_notes: "",
    length_cm: 30,
    width_cm: 20,
    height_cm: 15,
    weight_kg: 2.0,
    colli_count: 1,
  });
  const [busy, setBusy] = React.useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await businessApi().get("/business/pickup-locations");
        const active = (data.items || []).filter((l) => l.active !== false);
        setLocations(active);
        if (active.length > 0 && !f.pickup_location_id) {
          setF((p) => ({ ...p, pickup_location_id: active[0].id, product: active[0].default_product || p.product }));
        }
      } catch { toast.error("Kunne ikke hente pickup-lokationer"); }
    })();
  }, []);

  const weightWarn = parseFloat(f.weight_kg) > MAX_WEIGHT;
  const pcWarn = f.delivery_postal_code.length >= 4 && !isSjælland(f.delivery_postal_code);
  const blocked = weightWarn || pcWarn || !f.pickup_location_id;

  const submit = async (e) => {
    e.preventDefault();
    if (blocked) return;
    setBusy(true);
    try {
      const body = { ...f };
      if (!body.customer_reference) delete body.customer_reference;
      if (!body.recipient_email) delete body.recipient_email;
      if (!body.delivery_notes) delete body.delivery_notes;
      body.length_cm = parseFloat(body.length_cm);
      body.width_cm = parseFloat(body.width_cm);
      body.height_cm = parseFloat(body.height_cm);
      body.weight_kg = parseFloat(body.weight_kg);
      body.colli_count = parseInt(body.colli_count, 10) || 1;
      const { data } = await businessApi().post("/business/shipments", body);
      toast.success(`Forsendelse oprettet: ${data.shipment_number}`);
      nav(`/business/shipments/${data.id}`, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke oprette");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5 max-w-4xl" data-testid="business-shipment-create-page">
      <div>
        <div className="text-xs uppercase tracking-wider text-brand-red font-semibold">Ny forsendelse</div>
        <h1 className="font-display font-bold text-3xl text-brand-dark mt-1">Opret forsendelse</h1>
      </div>

      {locations.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 p-3 text-sm flex items-start gap-2" data-testid="biz-create-no-location">
          <AlertTriangle className="text-amber-600 mt-0.5 flex-shrink-0" size={18}/>
          <div>
            <div className="font-semibold text-amber-900">Ingen pickup-lokationer</div>
            <div className="text-amber-800 text-xs mt-1">Opret en pickup-lokation først under &quot;Pickup-lokationer&quot;.</div>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="bg-white border border-brand p-5 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        <Section title="Afhentning + produkt"/>
        <div>
          <label className="label-flat">Pickup-lokation *</label>
          <select className="input-flat" required value={f.pickup_location_id} onChange={(e) => set("pickup_location_id", e.target.value)} data-testid="biz-create-pickup-loc">
            <option value="">— Vælg —</option>
            {locations.map((l) => {
              const label = `${l.name} (${l.postal_code} ${l.city})`;
              return <option key={l.id} value={l.id}>{label}</option>;
            })}
          </select>
        </div>
        <div>
          <label className="label-flat">Produkt / service-niveau *</label>
          <select className="input-flat" value={f.product} onChange={(e) => set("product", e.target.value)} data-testid="biz-create-product">
            <option value="same_day_b2b">Same Day Business (cutoff 14:00)</option>
            <option value="next_day_b2b">Next Day Business</option>
          </select>
        </div>
        <div>
          <label className="label-flat">Din reference (idempotency)</label>
          <input className="input-flat" value={f.customer_reference} onChange={(e) => set("customer_reference", e.target.value)} placeholder="ORDER-12345" data-testid="biz-create-ref"/>
        </div>

        <Section title="Modtager"/>
        <div><label className="label-flat">Modtager *</label><input className="input-flat" required value={f.recipient_name} onChange={(e) => set("recipient_name", e.target.value)} data-testid="biz-create-recipient-name"/></div>
        <div><label className="label-flat">Telefon *</label><input className="input-flat" required value={f.recipient_phone} onChange={(e) => set("recipient_phone", e.target.value)} placeholder="+4520202020" data-testid="biz-create-recipient-phone"/></div>
        <div className="md:col-span-2"><label className="label-flat">Email (valgfri)</label><input className="input-flat" type="email" value={f.recipient_email} onChange={(e) => set("recipient_email", e.target.value)} data-testid="biz-create-recipient-email"/></div>
        <div className="md:col-span-2"><label className="label-flat">Leverings-adresse *</label><input className="input-flat" required value={f.delivery_address} onChange={(e) => set("delivery_address", e.target.value)} data-testid="biz-create-address"/></div>
        <div>
          <label className="label-flat">Postnr *</label>
          <input className="input-flat" required maxLength={4} value={f.delivery_postal_code} onChange={(e) => set("delivery_postal_code", e.target.value.replace(/\D/g, ""))} data-testid="biz-create-postal"/>
          {pcWarn && <div className="text-xs text-red-600 mt-1" data-testid="biz-create-pc-warn">Uden for Sjælland — kontakt EMA for særskilt aftale</div>}
        </div>
        <div><label className="label-flat">By *</label><input className="input-flat" required value={f.delivery_city} onChange={(e) => set("delivery_city", e.target.value)} data-testid="biz-create-city"/></div>
        <div className="md:col-span-2"><label className="label-flat">Adgangs-/leverings-instruktioner (synlige for chauffør)</label>
          <textarea className="input-flat" rows={2} value={f.delivery_notes} onChange={(e) => set("delivery_notes", e.target.value)} data-testid="biz-create-notes"/>
        </div>

        <Section title="Pakke-mål"/>
        <div><label className="label-flat">Længde (cm) *</label><input className="input-flat" type="number" min="1" step="0.1" required value={f.length_cm} onChange={(e) => set("length_cm", e.target.value)} data-testid="biz-create-length"/></div>
        <div><label className="label-flat">Bredde (cm) *</label><input className="input-flat" type="number" min="1" step="0.1" required value={f.width_cm} onChange={(e) => set("width_cm", e.target.value)} data-testid="biz-create-width"/></div>
        <div><label className="label-flat">Højde (cm) *</label><input className="input-flat" type="number" min="1" step="0.1" required value={f.height_cm} onChange={(e) => set("height_cm", e.target.value)} data-testid="biz-create-height"/></div>
        <div>
          <label className="label-flat">Vægt (kg) *</label>
          <input className="input-flat" type="number" min="0.1" step="0.1" max={MAX_WEIGHT} required value={f.weight_kg} onChange={(e) => set("weight_kg", e.target.value)} data-testid="biz-create-weight"/>
          {weightWarn && <div className="text-xs text-red-600 mt-1" data-testid="biz-create-weight-warn">Over {MAX_WEIGHT} kg — kontakt EMA</div>}
        </div>
        <div><label className="label-flat">Antal colli</label><input className="input-flat" type="number" min="1" max="20" value={f.colli_count} onChange={(e) => set("colli_count", e.target.value)} data-testid="biz-create-colli"/></div>

        <div className="md:col-span-2 flex flex-wrap gap-2 pt-3 border-t border-gray-100">
          <div className="text-xs text-brand-text2 mr-auto inline-flex items-center gap-1">
            <Calculator size={12}/> Pris beregnes ud fra din aktive aftale + volumen
          </div>
          <button type="button" onClick={() => nav(-1)} className="btn-secondary">Annullér</button>
          <button type="submit" disabled={busy || blocked} className="btn-primary" data-testid="biz-create-submit">
            {busy ? <Loader2 className="animate-spin" size={14}/> : <Save size={14}/>} Opret forsendelse
          </button>
        </div>
      </form>
    </div>
  );
}

function Section({ title }) {
  return <div className="md:col-span-2 text-xs uppercase tracking-wider text-brand-red font-semibold mt-2 pb-1 border-b border-gray-100">{title}</div>;
}
